import { redirect } from 'next/navigation'

export interface User {
  id: number
  username: string
  email?: string
  full_name?: string
  role: 'admin' | 'doctor' | 'nurse' | 'viewer'
  department?: string
  is_active: boolean
  created_at: string
  last_login?: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
  user: User
}

class AuthService {
  private static TOKEN_KEY = 'soma_auth_token'
  private static USER_KEY = 'soma_user'

  static setToken(token: string) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.TOKEN_KEY, token)
    }
  }

  static getToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(this.TOKEN_KEY)
    }
    return null
  }

  static removeToken() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.TOKEN_KEY)
      localStorage.removeItem(this.USER_KEY)
    }
  }

  static setUser(user: User) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user))
    }
  }

  static getUser(): User | null {
    if (typeof window !== 'undefined') {
      const userStr = localStorage.getItem(this.USER_KEY)
      if (userStr) {
        try {
          return JSON.parse(userStr)
        } catch {
          return null
        }
      }
    }
    return null
  }

  static isAuthenticated(): boolean {
    return this.getToken() !== null
  }

  static async login(username: string, password: string): Promise<LoginResponse> {
    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)

    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        let error
        try {
          error = await response.json()
        } catch {
          error = { detail: '서버 연결에 실패했습니다' }
        }
        throw new Error(error.detail || '로그인에 실패했습니다')
      }

      const data = await response.json()
      this.setToken(data.access_token)
      this.setUser(data.user)
      return data
    } catch (error) {
      console.error('Login error:', error)
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('서버에 연결할 수 없습니다. 서버가 실행 중인지 확인해주세요.')
      }
      throw error
    }
  }

  static async logout() {
    const token = this.getToken()
    if (token) {
      try {
        await fetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      } catch (error) {
        console.error('Logout error:', error)
      }
    }
    this.removeToken()
  }

  static async verifyToken(): Promise<boolean> {
    const token = this.getToken()
    if (!token) return false

    // 더미 관리자 토큰의 경우 항상 유효한 것으로 처리
    if (token === 'dummy-admin-token') {
      return true
    }

    try {
      const response = await fetch('/api/v1/auth/verify-token', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        this.removeToken()
        return false
      }

      return true
    } catch {
      this.removeToken()
      return false
    }
  }

  static async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken()

    if (!token) {
      throw new Error('인증이 필요합니다')
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    }

    const response = await fetch(url, {
      ...options,
      headers
    })

    if (response.status === 401) {
      this.removeToken()
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }

    return response
  }

  static hasRole(requiredRoles: string[]): boolean {
    const user = this.getUser()
    if (!user) return false

    if (user.role === 'admin') return true
    return requiredRoles.includes(user.role)
  }

  static canAccessPatientData(): boolean {
    return this.hasRole(['admin', 'doctor', 'nurse'])
  }

  static canModifySettings(): boolean {
    return this.hasRole(['admin'])
  }

  static canExportData(): boolean {
    return this.hasRole(['admin', 'doctor'])
  }
}

export default AuthService